import { useMemo, useState } from "react";
import { viewFromApi } from "@/components/FileViewer";
import { ChevronDown, ChevronUp, FileDown, Search, Users, Clock, CalendarDays, Activity, ArrowUpDown } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { fmtDur } from "@/components/care/ClientStayDialog";
import { openStayReport } from "@/components/care/StayHistory";

const monthLabel = (m) => new Date(m + "-01T12:00:00").toLocaleDateString([], { month: "long", year: "numeric" });

const openSummaryPdf = (month) => viewFromApi(api, "/stays/summary.pdf", { params: { month }, title: `Attendance summary · ${month}`, filename: `attendance-summary-${month}.pdf`, type: "application/pdf" });

function Kpi({ icon: Icon, label, value, sub, testid }) {
  return (
    <div className="flex items-center gap-3 px-5 py-4" data-testid={testid}>
      <div className="h-9 w-9 rounded-lg bg-primary/10 text-primary inline-flex items-center justify-center shrink-0"><Icon className="h-4 w-4" /></div>
      <div className="min-w-0">
        <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">{label}</div>
        <div className="font-display text-xl font-bold text-slate-900 leading-tight">{value}</div>
        {sub && <div className="text-[11px] text-slate-500">{sub}</div>}
      </div>
    </div>
  );
}

const COLS = [["client_name", "Client", "left"], ["days", "Days", "right"], ["stays", "Stays", "right"], ["avg", "Avg / day", "right"], ["minutes", "Total hours", "right"]];

export function MonthlySummary({ summary, onOpenClient }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState({ key: "minutes", dir: -1 });
  const tot = summary.totals;

  const rows = useMemo(() => {
    const list = summary.clients.map((c) => ({ ...c, avg: c.days ? Math.round(c.minutes / c.days) : 0 }))
      .filter((c) => !q || c.client_name.toLowerCase().includes(q.toLowerCase()));
    return list.sort((a, b) => {
      const va = a[sort.key], vb = b[sort.key];
      return (typeof va === "string" ? va.localeCompare(vb) : va - vb) * sort.dir;
    });
  }, [summary.clients, q, sort]);

  const toggleSort = (key) => setSort((s) => (s.key === key ? { key, dir: -s.dir } : { key, dir: key === "client_name" ? 1 : -1 }));

  return (
    <section className="mb-6 rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="stays-summary">
      <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 border-b border-slate-100">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">Month at a glance</div>
          <div className="font-display text-base font-bold text-slate-900" data-testid="summary-month">{monthLabel(summary.month)}</div>
        </div>
        <button onClick={() => openSummaryPdf(summary.month)} data-testid="summary-all-pdf" className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-3.5 h-9 text-xs font-semibold text-white hover:bg-slate-800 transition">
          <FileDown className="h-3.5 w-3.5" /> Attendance summary PDF
        </button>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-y lg:divide-y-0 divide-slate-100">
        <Kpi icon={Users} label="Clients served" value={tot.clients} sub={tot.on_site ? `${tot.on_site} on site now` : "none on site"} testid="kpi-clients" />
        <Kpi icon={Clock} label="Total hours" value={fmtDur(tot.minutes)} sub={tot.clients ? `${fmtDur(Math.round(tot.minutes / tot.clients))} avg per client` : null} testid="kpi-hours" />
        <Kpi icon={Activity} label="Stays" value={tot.stays} sub={tot.days ? `${(tot.stays / tot.days).toFixed(1)} per service day` : null} testid="kpi-stays" />
        <Kpi icon={CalendarDays} label="Service days" value={tot.days} sub="days with attendance" testid="kpi-days" />
      </div>
      <button onClick={() => setOpen((o) => !o)} data-testid="summary-toggle-breakdown" className="w-full flex items-center justify-between px-5 py-2.5 border-t border-slate-100 bg-slate-50/60 text-xs font-semibold text-slate-700 hover:bg-slate-100 transition">
        <span>Per-client breakdown · {summary.clients.length} client{summary.clients.length === 1 ? "" : "s"}</span>
        {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      </button>
      {open && (
        <div className="border-t border-slate-100" data-testid="summary-breakdown">
          <div className="px-4 py-2.5 flex items-center justify-between gap-3 bg-white">
            <div className="relative w-full max-w-xs">
              <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-slate-400" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a client…" data-testid="summary-search" className="h-8 w-full rounded-md border border-slate-200 bg-white pl-8 pr-3 text-xs outline-none focus:border-primary" />
            </div>
            <div className="text-[11px] text-slate-500 whitespace-nowrap">{rows.length} of {summary.clients.length}</div>
          </div>
          <div className="max-h-[360px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-slate-50 text-[11px] uppercase tracking-widest text-slate-500 shadow-[0_1px_0_0_#e2e8f0]">
                <tr>
                  {COLS.map(([key, label, align]) => (
                    <th key={key} className={`px-4 py-2 ${align === "right" ? "text-right" : "text-left"}`}>
                      <button onClick={() => toggleSort(key)} data-testid={`summary-sort-${key}`} className={`inline-flex items-center gap-1 hover:text-slate-800 ${sort.key === key ? "text-primary" : ""}`}>{label} <ArrowUpDown className="h-3 w-3" /></button>
                    </th>
                  ))}
                  <th className="px-4 py-2 text-right">Report</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.length === 0 && <tr><td colSpan={6} className="py-6 text-center text-slate-400 text-xs">No clients match.</td></tr>}
                {rows.map((c) => (
                  <tr key={c.client_id} data-testid={`summary-${c.client_id}`} className="hover:bg-slate-50/70">
                    <td className="px-4 py-2"><button onClick={() => onOpenClient(c.client_id)} className="font-medium text-slate-900 hover:text-primary hover:underline text-left">{c.client_name}</button></td>
                    <td className="px-4 py-2 text-right tabular-nums text-slate-600">{c.days}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-slate-600">{c.stays}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-slate-600">{c.days ? fmtDur(c.avg) : "—"}</td>
                    <td className="px-4 py-2 text-right tabular-nums font-semibold text-slate-900">{fmtDur(c.minutes)}</td>
                    <td className="px-4 py-2 text-right">
                      <button onClick={() => openStayReport(c.client_id, summary.month)} data-testid={`summary-pdf-${c.client_id}`} title="Monthly stay report PDF" className="inline-flex items-center gap-1 text-[11px] font-medium text-primary hover:underline"><FileDown className="h-3 w-3" /> PDF</button>
                    </td>
                  </tr>
                ))}
              </tbody>
              {rows.length > 0 && (
                <tfoot className="sticky bottom-0 bg-slate-50 text-xs font-semibold text-slate-800 shadow-[0_-1px_0_0_#e2e8f0]">
                  <tr>
                    <td className="px-4 py-2">Total</td>
                    <td className="px-4 py-2 text-right tabular-nums">{tot.days}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{rows.reduce((a, c) => a + c.stays, 0)}</td>
                    <td className="px-4 py-2" />
                    <td className="px-4 py-2 text-right tabular-nums">{fmtDur(rows.reduce((a, c) => a + c.minutes, 0))}</td>
                    <td />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      )}
    </section>
  );
}
